import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from '../security/msal';
import { LandingComponent } from './features/landing/landing.component';
import { PageNotFoundComponent } from './features/page-not-found/page-not-found.component';
import { FooterComponent } from './features/footer/footer.component';
import { HeaderComponent } from './features/header/header.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AdminCenterComponent } from './features/admin-center/admin-center.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule } from '@angular/material/dialog';
import { LockUserDialogComponent } from './features/dialogs/lock-user-dialog/lock-user-dialog/lock-user-dialog.component';
import { EditUserDialogComponent } from './features/dialogs/edit-user-dialog/edit-user-dialog.component';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { DataUploaderComponent } from './features/data-uploader/data-uploader.component';
import { MatIconModule } from '@angular/material/icon';
import { DatePipe } from '@angular/common';
import { AssumptionSettingsComponent } from './features/assumption-settings/assumption-settings.component';
import { MemberSettingsComponent } from './features/member-settings/member-settings.component';
import { CalculatorResultsComponent } from './features/calculator-results/calculator-results.component';
import { InfoDialogComponent } from './features/dialogs/info-dialog/info-dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { CurrencyPipe } from '@angular/common';
import {
  IConfig,
  NgxMaskDirective,
  NgxMaskPipe,
  provideEnvironmentNgxMask,
  provideNgxMask,
} from 'ngx-mask';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ReportComponent } from './features/report/report.component';
import { TermsAndConditionsInfoComponent } from './features/dialogs/terms-and-conditions-info/terms-and-conditions-info.component';
import { ActuarialAssumptionsInfoComponent } from './features/dialogs/actuarial-assumptions-info/actuarial-assumptions-info.component';
import { AveragePensionableEarningsInfoComponent } from './features/dialogs/average-pensionable-earnings-info/average-pensionable-earnings-info.component';
import { AveragePensionableEarningsEspsInfoComponent } from './features/dialogs/average-pensionable-earnings-esps-info/average-pensionable-earnings-esps-info.component';
import { YearlySalaryInfoComponent } from './features/dialogs/yearly-salary-info/yearly-salary-info.component';
import { FuturePercentageInfoComponent } from './features/dialogs/future-percentage-info/future-percentage-info.component';
import { EffectiveDateInfoComponent } from './features/dialogs/effective-date-info/effective-date-info.component';
import { AddRetirementDateInfoComponent } from './features/dialogs/add-retirement-date-info/add-retirement-date-info.component';
import { FormOfPensionPaymentInfoComponent } from './features/dialogs/form-of-pension-payment-info/form-of-pension-payment-info.component';
import { PrintDialogDumpComponent } from './features/dialogs/print-dialog-dump/print-dialog-dump.component';
import { ImpersonationComponent } from './features/impersonation/impersonation.component';
import { FormOfPensionPaymentMarriedInfoComponent } from './features/dialogs/form-of-pension-payment-married-info/form-of-pension-payment-married-info.component';
import { AveragePensionableEarningsSpsInfoComponent } from './features/dialogs/average-pensionable-earnings-sps-info/average-pensionable-earnings-sps-info.component';
import { InvalidPermissionsComponent } from './features/invalid-permissions/invalid-permissions.component';
import { UnderMaintenanceComponent } from './features/under-maintenance/under-maintenance.component';
import { ComparisonRetirementTypesComponent } from './features/dialogs/comparison-retirement-types/comparison-retirement-types.component';
import { ComparisonRetirementTypesEspsComponent } from './features/dialogs/comparison-retirement-types-esps/comparison-retirement-types-esps.component';
import { DepartureDateComponent } from './features/dialogs/departure-date/departure-date.component';

const maskConfigFunction: () => Partial<IConfig> = () => {
  return {
    validation: false,
    thousandSeparator: ',',
    decimalMarker: '.'
  };
};

@NgModule({
  declarations: [
    AppComponent,
    LandingComponent,
    PageNotFoundComponent,
    FooterComponent,
    HeaderComponent,
    AdminCenterComponent,
    LockUserDialogComponent,
    EditUserDialogComponent,
    DataUploaderComponent,
    AssumptionSettingsComponent,
    MemberSettingsComponent,
    CalculatorResultsComponent,
    InfoDialogComponent,
    ReportComponent,
    TermsAndConditionsInfoComponent,
    ActuarialAssumptionsInfoComponent,
    AveragePensionableEarningsInfoComponent,
    AveragePensionableEarningsEspsInfoComponent,
    YearlySalaryInfoComponent,
    FuturePercentageInfoComponent,
    EffectiveDateInfoComponent,
    AddRetirementDateInfoComponent,
    FormOfPensionPaymentInfoComponent,
    PrintDialogDumpComponent,
    ImpersonationComponent,
    FormOfPensionPaymentMarriedInfoComponent,
    AveragePensionableEarningsSpsInfoComponent,
    InvalidPermissionsComponent,
    UnderMaintenanceComponent,
    ComparisonRetirementTypesComponent,
    ComparisonRetirementTypesEspsComponent,
    DepartureDateComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    MatButtonModule,
    MatToolbarModule,
    MatListModule,
    MatMenuModule,
    HttpClientModule,
    MsalModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatTabsModule,
    FormsModule,
    MatTableModule,
    MatDialogModule,
    MatButtonToggleModule,
    ReactiveFormsModule,
    MatChipsModule,
    MatIconModule,
    DatePipe,
    MatDatepickerModule,
    MatNativeDateModule,
    CommonModule,
    CurrencyPipe,
    NgxMaskDirective,
    NgxMaskPipe,
    MatProgressBarModule,
    MatRadioModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    DatePipe,
    CurrencyPipe,
    provideNgxMask(maskConfigFunction),
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
